/**
 * Command list for the command palette (issue 198).
 *
 * Navigation targets are DERIVED from the Family registry via `allFamilies()`
 * — the same source the header nav reads (05 §R1) — so a Family authored this
 * afternoon appears in the palette with no code written for it, under the icon
 * rule `entityIcons` already applies to search rows.
 *
 * What stays local is what the registry does not carry: the platform tools
 * (Shop/Treasure/Tracker are pages, not Families — the same local list
 * `Layout.js` keeps), the signed-in destinations, and the two global actions
 * (theme toggle, system switch). Per-page controls — reader mode, print,
 * copy-as-stat-block, the random-entry draw — are deliberately NOT here: they
 * are contextual to a detail/list page, and how that action surface scales is
 * issue 287's question, not this one's.
 */

import {
  Home, Store, Gem, Swords, Heart, User, Hammer, UserCog, Moon, Sun, Globe, AlertTriangle,
} from 'lucide-react';
import { allFamilies } from '../config/familyRegistry';
import entityTypeIcon from './entityIcons';

/**
 * Build the full command list for the current context.
 *
 * Every command is `{ id, section, label, icon, href | run }` — `href` rows
 * navigate (through the caller's router, so state.from applies), `run` rows
 * execute and close.
 */
export function buildCommands({
  systemSlug,
  systems = [],
  isAuthenticated = false,
  isDarkMode = true,
  onToggleTheme = null,
  onSwitchSystem = null,
} = {}) {
  const commands = [];

  // Registry-derived navigation — one row per nav-ordered Family.
  for (const family of allFamilies(systemSlug)) {
    commands.push({
      id: `go-${family.segment}`,
      section: 'Go to',
      label: family.labels.plural,
      icon: entityTypeIcon(family.segment),
      href: `/${family.segment}`,
    });
  }

  // Platform tools — pages, not Families; not in the registry.
  commands.push(
    { id: 'go-home', section: 'Go to', label: 'Home', icon: Home, href: '/' },
    { id: 'go-shop', section: 'Go to', label: 'Shop Generator', icon: Store, href: '/shop' },
    { id: 'go-treasure', section: 'Go to', label: 'Treasure Generator', icon: Gem, href: '/treasure' },
    { id: 'go-trap-builder', section: 'Go to', label: 'Trap Builder', icon: AlertTriangle, href: '/trap-builder' },
    { id: 'go-tracker', section: 'Go to', label: 'Initiative Tracker', icon: Swords, href: '/tracker' },
  );

  if (isAuthenticated) {
    commands.push(
      { id: 'go-favorites', section: 'Go to', label: 'Favorites', icon: Heart, href: '/favorites' },
      { id: 'go-characters', section: 'Go to', label: 'Characters', icon: User, href: '/characters' },
      { id: 'go-authoring', section: 'Go to', label: 'My content', icon: Hammer, href: '/authoring' },
      { id: 'go-account', section: 'Go to', label: 'Account', icon: UserCog, href: '/account' },
    );
  }

  if (onToggleTheme) {
    commands.push({
      id: 'toggle-theme',
      section: 'Actions',
      label: isDarkMode ? 'Switch to light theme' : 'Switch to dark theme',
      icon: isDarkMode ? Sun : Moon,
      run: onToggleTheme,
    });
  }

  if (onSwitchSystem) {
    for (const s of systems) {
      if (!s || s.slug === systemSlug) continue;
      commands.push({
        id: `system-${s.slug}`,
        section: 'Actions',
        label: `Switch to ${s.name}`,
        icon: Globe,
        run: () => onSwitchSystem(s.slug),
      });
    }
  }

  return commands;
}

/** Case-insensitive substring filter on the label. Empty query keeps all. */
export function filterCommands(commands, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return commands;
  return commands.filter((c) => c.label.toLowerCase().includes(q));
}
